import { cn } from '@realtyfeed/utils';
import { forwardRef } from 'react';

export const AVATAR_SIZES = ['sm', 'md', 'lg', 'xl'] as const;
export type AvatarSize = (typeof AVATAR_SIZES)[number];

export const AVATAR_STATUSES = ['online', 'away', 'busy', 'offline'] as const;
export type AvatarStatus = (typeof AVATAR_STATUSES)[number];

const sizeClasses: Record<AvatarSize, string> = {
  sm: 'h-6 w-6 text-xs',
  md: 'h-8 w-8 text-sm',
  lg: 'h-10 w-10 text-base',
  xl: 'h-14 w-14 text-lg',
};

const statusColorClasses: Record<AvatarStatus, string> = {
  online: 'bg-emerald-400',
  away: 'bg-amber-400',
  busy: 'bg-red-400',
  offline: 'bg-slate-400 dark:bg-slate-500',
};

type AvatarProps = React.HTMLAttributes<HTMLElement> & {
  as?: React.ElementType;
  img?: string;
  size?: AvatarSize;
  icon?: React.ReactNode;
  color?: string;
  status?: AvatarStatus;
  content?: string;
  initials?: boolean;
  badgeText?: string | number;
  badgeColor?: string;
};

function getInitials(content: string): string {
  return content
    .split(' ')
    .map((word) => word[0])
    .join('');
}

export const Avatar = forwardRef<HTMLElement, AvatarProps>(
  (
    {
      as: Tag = 'div',
      img,
      size = 'md',
      icon,
      color,
      status,
      content,
      initials,
      badgeText,
      badgeColor,
      className,
      style,
      ...rest
    },
    ref
  ) => {
    return (
      <Tag
        ref={ref}
        className={cn(
          'relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-200 font-semibold text-slate-900 dark:bg-slate-800 dark:text-white',
          sizeClasses[size],
          className
        )}
        style={{ backgroundColor: color, ...style }}
        {...rest}
      >
        {img ? (
          <img src={img} alt="avatar" className="h-full w-full object-cover" />
        ) : (
          <span className="relative flex items-center justify-center">
            {initials && content ? getInitials(content) : content}
            {icon}
            {badgeText !== undefined ? (
              <span
                className="absolute -right-2 -top-2 flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] text-slate-950"
                style={{ backgroundColor: badgeColor ?? '#22d3ee' }}
              >
                {badgeText}
              </span>
            ) : null}
          </span>
        )}
        {status ? (
          <span
            className={cn(
              'absolute bottom-0 right-0 h-2 w-2 rounded-full ring-2 ring-slate-50 dark:ring-slate-950',
              statusColorClasses[status]
            )}
          />
        ) : null}
      </Tag>
    );
  }
);

Avatar.displayName = 'Avatar';
